import React, { useState } from 'react';
import { ArrowLeft, Save, Plus, Minus, Bookmark, Power, Users, Trash2, Edit3, GripVertical, FileUp } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Switch } from './ui/switch';
import { 
  AlertDialog, 
  AlertDialogAction, 
  AlertDialogCancel, 
  AlertDialogContent, 
  AlertDialogDescription, 
  AlertDialogFooter, 
  AlertDialogHeader, 
  AlertDialogTitle 
} from './ui/alert-dialog';
import { DndProvider, useDrag, useDrop } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';

interface GameTemplate {
  id: string;
  name: string;
  oversPerInnings: number;
  numberOfPlayers: number;
  ballsPerOver: number;
  customRules: {
    enableRetirement: boolean;
    retirementRuns: number;
    enableMaxBallsPerOver: boolean;
    maxBallsPerOver: number;
    lastOverExemption: boolean;
    enableLastManStands: boolean;
  };
  createdAt: Date;
}

interface SavedTeam {
  id: string;
  name: string;
  players: string[];
  createdAt: Date;
}

interface MatchSettingsProps {
  isOpen: boolean;
  onClose: () => void;
  settings: {
    oversPerInnings: number;
    numberOfPlayers: number;
    ballsPerOver: number;
    customRules: {
      enableRetirement: boolean;
      retirementRuns: number;
      enableMaxBallsPerOver: boolean;
      maxBallsPerOver: number;
      lastOverExemption: boolean;
      enableLastManStands: boolean;
    };
  };
  onSave: (settings: {
    oversPerInnings: number;
    numberOfPlayers: number;
    ballsPerOver: number;
    customRules: {
      enableRetirement: boolean;
      retirementRuns: number;
      enableMaxBallsPerOver: boolean;
      maxBallsPerOver: number;
      lastOverExemption: boolean;
      enableLastManStands: boolean;
    };
  }) => void;
  onSaveTemplate: (template: Omit<GameTemplate, 'id' | 'createdAt'>) => void;
  onEndGame: () => void;
  onEndInnings: () => void;
  teamAPlayers: string[];
  teamBPlayers: string[];
  onUpdateTeamPlayers: (teamA: string[], teamB: string[]) => void;
  savedTeams: SavedTeam[];
  onSaveTeam: (team: Omit<SavedTeam, 'id' | 'createdAt'>) => void;
  onOpenTeamsPage: () => void;
}

const DragDropPlayerRow = ({
  player,
  index,
  onEdit,
  onRemove,
  onMovePlayer,
}: {
  player: string;
  index: number;
  onEdit: (newName: string) => void;
  onRemove: () => void;
  onMovePlayer: (dragIndex: number, hoverIndex: number) => void;
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [editName, setEditName] = useState(player);

  const [{ isDragging }, drag] = useDrag({
    type: 'player',
    item: { index },
    collect: (monitor) => ({
      isDragging: monitor.isDragging(),
    }),
  });

  const [, drop] = useDrop({
    accept: 'player',
    hover: (draggedItem: { index: number }) => {
      if (draggedItem.index !== index) {
        onMovePlayer(draggedItem.index, index);
        draggedItem.index = index;
      }
    },
  });

  const handleSave = () => {
    if (editName.trim()) {
      onEdit(editName.trim());
      setIsEditing(false);
    }
  };

  const handleCancel = () => {
    setEditName(player);
    setIsEditing(false);
  };

  return (
    <div
      ref={(node) => drag(drop(node))}
      className={`flex items-center justify-between p-3 bg-white rounded-lg border border-gray-200 cursor-move ${
        isDragging ? 'opacity-50' : ''
      }`}
    >
      <div className="flex items-center gap-2 flex-1">
        <GripVertical className="w-4 h-4 text-gray-400" />
        {isEditing ? (
          <div className="flex items-center gap-2 flex-1">
            <Input
              value={editName}
              onChange={(e) => setEditName(e.target.value)}
              className="flex-1 text-base"
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  handleSave();
                } else if (e.key === 'Escape') {
                  handleCancel();
                }
              }}
              autoFocus
            />
            <Button size="sm" onClick={handleSave}>
              <Save className="w-4 h-4" />
            </Button>
            <Button size="sm" variant="outline" onClick={handleCancel}>
              Cancel
            </Button>
          </div>
        ) : (
          <>
            <span className="font-medium flex-1">{player}</span>
            <div className="flex gap-1">
              <button
                onClick={() => setIsEditing(true)}
                className="text-gray-400 hover:text-blue-500 p-1"
              >
                <Edit3 className="w-4 h-4" />
              </button>
              <button
                onClick={onRemove}
                className="text-gray-400 hover:text-red-500 p-1"
              >
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export function MatchSettings({ 
  isOpen, 
  onClose, 
  settings, 
  onSave, 
  onSaveTemplate, 
  onEndGame,
  onEndInnings,
  teamAPlayers,
  teamBPlayers,
  onUpdateTeamPlayers,
  savedTeams,
  onSaveTeam,
  onOpenTeamsPage
}: MatchSettingsProps) {
  const [formSettings, setFormSettings] = useState(settings);
  const [useCustomOvers, setUseCustomOvers] = useState(false);
  const [useCustomPlayers, setUseCustomPlayers] = useState(false);

  const [showSaveTemplate, setShowSaveTemplate] = useState(false);
  const [templateName, setTemplateName] = useState('');
  const [showEndGameDialog, setShowEndGameDialog] = useState(false);
  const [showEndInningsDialog, setShowEndInningsDialog] = useState(false);
  const [showTeamManagement, setShowTeamManagement] = useState(false);
  const [showSaveTeamDialog, setShowSaveTeamDialog] = useState(false);
  const [activeTeam, setActiveTeam] = useState<'A' | 'B'>('A');
  const [newPlayerName, setNewPlayerName] = useState('');
  const [teamName, setTeamName] = useState('');
  const [localTeamAPlayers, setLocalTeamAPlayers] = useState(teamAPlayers);
  const [localTeamBPlayers, setLocalTeamBPlayers] = useState(teamBPlayers);

  const maxPlayersForTeam = formSettings.numberOfPlayers;

  const handleSave = () => {
    onSave(formSettings);
    onUpdateTeamPlayers(localTeamAPlayers, localTeamBPlayers);
    onClose();
  };

  const handleSaveTemplate = () => {
    if (templateName.trim()) {
      onSaveTemplate({
        name: templateName.trim(),
        ...formSettings
      });
      setTemplateName('');
      setShowSaveTemplate(false);
    }
  };

  const handleEndGame = () => {
    onEndGame();
    setShowEndGameDialog(false);
    onClose();
  };

  const handleEndInnings = () => {
    onEndInnings();
    setShowEndInningsDialog(false);
    onClose();
  };

  const handleAddPlayer = () => {
    if (newPlayerName.trim()) {
      const currentTeamPlayers = activeTeam === 'A' ? localTeamAPlayers : localTeamBPlayers;
      if (currentTeamPlayers.length < maxPlayersForTeam) {
        if (activeTeam === 'A') {
          setLocalTeamAPlayers(prev => [...prev, newPlayerName.trim()]);
        } else {
          setLocalTeamBPlayers(prev => [...prev, newPlayerName.trim()]);
        }
        setNewPlayerName('');
      }
    }
  };

  const handleRemovePlayer = (playerName: string) => {
    if (activeTeam === 'A') {
      setLocalTeamAPlayers(prev => prev.filter(p => p !== playerName));
    } else {
      setLocalTeamBPlayers(prev => prev.filter(p => p !== playerName));
    }
  };

  const handleEditPlayer = (oldName: string, newName: string) => {
    if (newName.trim()) {
      if (activeTeam === 'A') {
        setLocalTeamAPlayers(prev => prev.map(p => p === oldName ? newName.trim() : p));
      } else {
        setLocalTeamBPlayers(prev => prev.map(p => p === oldName ? newName.trim() : p));
      }
    }
  };

  const handleMovePlayer = (dragIndex: number, hoverIndex: number) => {
    if (activeTeam === 'A') {
      setLocalTeamAPlayers(prev => {
        const newPlayers = [...prev];
        const draggedPlayer = newPlayers[dragIndex];
        newPlayers.splice(dragIndex, 1);
        newPlayers.splice(hoverIndex, 0, draggedPlayer);
        return newPlayers;
      });
    } else {
      setLocalTeamBPlayers(prev => {
        const newPlayers = [...prev];
        const draggedPlayer = newPlayers[dragIndex];
        newPlayers.splice(dragIndex, 1);
        newPlayers.splice(hoverIndex, 0, draggedPlayer);
        return newPlayers;
      });
    }
  };

  const handleSaveTeam = () => {
    if (teamName.trim()) {
      const currentTeamPlayers = activeTeam === 'A' ? localTeamAPlayers : localTeamBPlayers;
      onSaveTeam({
        name: teamName.trim(),
        players: currentTeamPlayers
      });
      setTeamName('');
      setShowSaveTeamDialog(false);
    }
  };

  const handleLoadTeam = (team: SavedTeam) => {
    // Load team players, but limit to current game format
    const playersToLoad = team.players.slice(0, maxPlayersForTeam);
    if (activeTeam === 'A') {
      setLocalTeamAPlayers(playersToLoad);
    } else {
      setLocalTeamBPlayers(playersToLoad);
    }
  };

  const handleNumberChange = (field: string, value: number, min: number, max: number) => {
    const clampedValue = Math.max(min, Math.min(max, value));
    if (field === 'oversPerInnings' || field === 'numberOfPlayers' || field === 'ballsPerOver') {
      setFormSettings(prev => ({ ...prev, [field]: clampedValue }));
      
      // If numberOfPlayers changed, trim team players if necessary
      if (field === 'numberOfPlayers') {
        setLocalTeamAPlayers(prev => prev.slice(0, clampedValue));
        setLocalTeamBPlayers(prev => prev.slice(0, clampedValue));
      }
    } else if (field === 'retirementRuns') {
      setFormSettings(prev => ({ 
        ...prev, 
        customRules: { ...prev.customRules, retirementRuns: clampedValue }
      }));
    } else if (field === 'maxBallsPerOver') {
      setFormSettings(prev => ({ 
        ...prev, 
        customRules: { ...prev.customRules, maxBallsPerOver: clampedValue }
      }));
    }
  };

  const NumberInput = ({ 
    label, 
    value, 
    field, 
    min = 1, 
    max = 999,
    step = 1 
  }: { 
    label: string;
    value: number;
    field: string;
    min?: number;
    max?: number;
    step?: number;
  }) => (
    <div className="space-y-2">
      <Label>{label}</Label>
      <div className="flex items-center gap-2">
        <Button
          variant="outline"
          size="sm"
          onClick={() => handleNumberChange(field, value - step, min, max)}
          disabled={value <= min}
        >
          <Minus className="w-4 h-4" />
        </Button>
        <Input
          type="number"
          value={value}
          onChange={(e) => handleNumberChange(field, parseInt(e.target.value) || min, min, max)}
          className="w-20 text-center"
          min={min}
          max={max}
        />
        <Button
          variant="outline"
          size="sm"
          onClick={() => handleNumberChange(field, value + step, min, max)}
          disabled={value >= max}
        >
          <Plus className="w-4 h-4" />
        </Button>
      </div>
    </div>
  );

  if (!isOpen) return null;

  if (showTeamManagement) {
    const currentTeamPlayers = activeTeam === 'A' ? localTeamAPlayers : localTeamBPlayers;
    const canAddMorePlayers = currentTeamPlayers.length < maxPlayersForTeam;
    
    return (
      <DndProvider backend={HTML5Backend}>
        <div className="fixed inset-0 bg-white z-50 flex flex-col">
          <div className="flex items-center justify-between p-4 border-b border-gray-200">
            <div className="flex items-center gap-3">
              <button
                onClick={() => setShowTeamManagement(false)}
                className="flex items-center justify-center w-8 h-8 rounded-full hover:bg-gray-100"
              >
                <ArrowLeft className="w-5 h-5 text-blue-500" />
              </button>
              <h1 className="text-lg font-semibold">Team Management</h1>
            </div>
            <div className="flex gap-2">
              <Button 
                onClick={() => setShowSaveTeamDialog(true)} 
                variant="outline" 
                size="sm"
                disabled={currentTeamPlayers.length === 0}
              >
                <Save className="w-4 h-4 mr-2" />
                Save Team
              </Button>
              <Button onClick={handleSave} size="sm">
                <Save className="w-4 h-4 mr-2" />
                Save
              </Button>
            </div>
          </div>

          {/* Team Tabs */}
          <div className="flex border-b border-gray-200">
            <button
              onClick={() => setActiveTeam('A')}
              className={`flex-1 py-3 px-4 text-center font-medium transition-colors ${
                activeTeam === 'A'
                  ? 'text-blue-600 border-b-2 border-blue-600'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              Team A ({localTeamAPlayers.length}/{maxPlayersForTeam} players)
            </button>
            <button
              onClick={() => setActiveTeam('B')}
              className={`flex-1 py-3 px-4 text-center font-medium transition-colors ${
                activeTeam === 'B'
                  ? 'text-blue-600 border-b-2 border-blue-600'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              Team B ({localTeamBPlayers.length}/{maxPlayersForTeam} players)
            </button>
          </div>

          <div className="flex-1 overflow-y-auto p-4">
            <div className="max-w-md mx-auto space-y-4">
              {/* Load Saved Team */}
              <div className="space-y-3 p-4 bg-blue-50 rounded-lg">
                <div className="flex items-center justify-between">
                  <h3 className="font-medium">Load Saved Team</h3>
                  <Button
                    onClick={onOpenTeamsPage}
                    variant="outline"
                    size="sm"
                  >
                    <FileUp className="w-4 h-4 mr-2" />
                    Manage Teams
                  </Button>
                </div>
                {savedTeams.length > 0 ? (
                  <Select onValueChange={(value) => {
                    const team = savedTeams.find(t => t.id === value);
                    if (team) handleLoadTeam(team);
                  }}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select a saved team" />
                    </SelectTrigger>
                    <SelectContent>
                      {savedTeams.map((team) => (
                        <SelectItem key={team.id} value={team.id}>
                          {team.name} ({Math.min(team.players.length, maxPlayersForTeam)} players)
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                ) : (
                  <p className="text-sm text-gray-600">No saved teams available</p>
                )}
              </div>

              {/* Add Player */}
              <div className="space-y-3 p-4 bg-gray-50 rounded-lg">
                <h3 className="font-medium">Add New Player</h3>
                <div className="flex gap-2">
                  <Input
                    value={newPlayerName}
                    onChange={(e) => setNewPlayerName(e.target.value)}
                    placeholder="Enter player name"
                    className="text-base"
                    disabled={!canAddMorePlayers}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        handleAddPlayer();
                      }
                    }}
                  />
                  <Button 
                    onClick={handleAddPlayer} 
                    disabled={!newPlayerName.trim() || !canAddMorePlayers}
                  >
                    <Plus className="w-4 h-4" />
                  </Button>
                </div>
                {!canAddMorePlayers && (
                  <p className="text-sm text-amber-600">
                    Maximum {maxPlayersForTeam} players reached for current game format
                  </p>
                )}
              </div>

              {/* Player List */}
              <div className="space-y-2">
                <h3 className="font-medium">
                  Team {activeTeam} Players ({currentTeamPlayers.length}/{maxPlayersForTeam})
                </h3>
                {currentTeamPlayers.length === 0 ? (
                  <div className="text-center text-gray-500 py-8">
                    <Users className="w-8 h-8 mx-auto mb-2 text-gray-400" />
                    <p>No players added yet</p>
                    <p className="text-sm">Add players using the form above</p>
                  </div>
                ) : (
                  <div className="space-y-2">
                    <p className="text-sm text-gray-600">
                      Drag players to reorder them
                    </p>
                    {currentTeamPlayers.map((player, index) => (
                      <DragDropPlayerRow
                        key={`${activeTeam}-${index}-${player}`}
                        player={player}
                        index={index}
                        onEdit={(newName) => handleEditPlayer(player, newName)}
                        onRemove={() => handleRemovePlayer(player)}
                        onMovePlayer={handleMovePlayer}
                      />
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </DndProvider>
    );
  }

  return (
    <>
      <div className="fixed inset-0 bg-white z-50 flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-200">
          <div className="flex items-center gap-3">
            <button
              onClick={onClose}
              className="flex items-center justify-center w-8 h-8 rounded-full hover:bg-gray-100"
            >
              <ArrowLeft className="w-5 h-5 text-blue-500" />
            </button>
            <h1 className="text-lg font-semibold">Game Settings</h1>
          </div>
          <div className="flex gap-2">
            <Button onClick={() => setShowSaveTemplate(true)} variant="outline" size="sm">
              <Bookmark className="w-4 h-4 mr-2" />
              Save Template
            </Button>
            <Button onClick={handleSave} size="sm">
              <Save className="w-4 h-4 mr-2" />
              Save
            </Button>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-4">
          <div className="max-w-md mx-auto space-y-6">
            
            {/* 1. Game Format Section */}
            <div className="space-y-4">
              <h3 className="font-medium text-lg">Game Format</h3>
              
              {/* Overs per Innings */}
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <Label>Overs per Innings</Label>
                  <div className="flex items-center gap-2">
                    <Label htmlFor="custom-overs" className="text-sm">Custom</Label>
                    <Switch 
                      id="custom-overs"
                      checked={useCustomOvers}
                      onCheckedChange={setUseCustomOvers}
                    />
                  </div>
                </div>
                
                {useCustomOvers ? (
                  <NumberInput
                    label=""
                    value={formSettings.oversPerInnings}
                    field="oversPerInnings"
                    min={1}
                    max={90}
                  />
                ) : (
                  <Select
                    value={formSettings.oversPerInnings.toString()}
                    onValueChange={(value) => 
                      setFormSettings(prev => ({ ...prev, oversPerInnings: parseInt(value) }))
                    }
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="5">5 Overs</SelectItem>
                      <SelectItem value="8">8 Overs</SelectItem>
                      <SelectItem value="10">10 Overs</SelectItem>
                      <SelectItem value="20">20 Overs (T20)</SelectItem>
                      <SelectItem value="50">50 Overs (ODI)</SelectItem>
                      <SelectItem value="90">90 Overs (Test - Day 1)</SelectItem>
                    </SelectContent>
                  </Select>
                )}
              </div>

              {/* Number of Players */}
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <Label>Players per Team</Label>
                  <div className="flex items-center gap-2">
                    <Label htmlFor="custom-players" className="text-sm">Custom</Label>
                    <Switch 
                      id="custom-players"
                      checked={useCustomPlayers}
                      onCheckedChange={setUseCustomPlayers}
                    />
                  </div>
                </div>
                
                {useCustomPlayers ? (
                  <NumberInput
                    label=""
                    value={formSettings.numberOfPlayers}
                    field="numberOfPlayers"
                    min={6}
                    max={15}
                  />
                ) : (
                  <Select
                    value={formSettings.numberOfPlayers.toString()}
                    onValueChange={(value) => {
                      const newPlayerCount = parseInt(value);
                      setFormSettings(prev => ({ ...prev, numberOfPlayers: newPlayerCount }));
                      // Trim team players if necessary
                      setLocalTeamAPlayers(prev => prev.slice(0, newPlayerCount));
                      setLocalTeamBPlayers(prev => prev.slice(0, newPlayerCount));
                    }}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="6">6 Players</SelectItem>
                      <SelectItem value="8">8 Players</SelectItem>
                      <SelectItem value="11">11 Players (Standard)</SelectItem>
                      <SelectItem value="15">15 Players (Extended Squad)</SelectItem>
                    </SelectContent>
                  </Select>
                )}
              </div>

              {/* Balls per Over */}
              <div className="space-y-3">
                <NumberInput
                  label="Balls per Over"
                  value={formSettings.ballsPerOver}
                  field="ballsPerOver"
                  min={4}
                  max={8}
                />
              </div>
            </div>

            {/* 2. Custom Rules Section */}
            <div className="space-y-4 border-t pt-6">
              <h3 className="font-medium text-lg">Custom Rules</h3>
              
              {/* Retirement Rule */}
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <div>
                    <Label>Batter Retirement</Label>
                    <p className="text-sm text-gray-600">Auto-retire batters after reaching run limit</p>
                  </div>
                  <Switch 
                    checked={formSettings.customRules.enableRetirement}
                    onCheckedChange={(checked) => 
                      setFormSettings(prev => ({ 
                        ...prev, 
                        customRules: { ...prev.customRules, enableRetirement: checked }
                      }))
                    }
                  />
                </div>
                
                {formSettings.customRules.enableRetirement && (
                  <NumberInput
                    label="Retirement at (runs)"
                    value={formSettings.customRules.retirementRuns}
                    field="retirementRuns"
                    min={10}
                    max={200}
                    step={5}
                  />
                )}
              </div>

              {/* Max Balls per Over Rule */}
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <div>
                    <Label>Max Balls per Over</Label>
                    <p className="text-sm text-gray-600">Limit total balls per over (including extras)</p>
                  </div>
                  <Switch 
                    checked={formSettings.customRules.enableMaxBallsPerOver}
                    onCheckedChange={(checked) => 
                      setFormSettings(prev => ({ 
                        ...prev, 
                        customRules: { ...prev.customRules, enableMaxBallsPerOver: checked }
                      }))
                    }
                  />
                </div>
                
                {formSettings.customRules.enableMaxBallsPerOver && (
                  <>
                    <NumberInput
                      label="Max balls per over"
                      value={formSettings.customRules.maxBallsPerOver}
                      field="maxBallsPerOver"
                      min={7}
                      max={15}
                    />
                    
                    <div className="flex items-center justify-between">
                      <div>
                        <Label>Last Over Exemption</Label>
                        <p className="text-sm text-gray-600">Last over follows normal cricket rules (6 legal deliveries)</p>
                      </div>
                      <Switch 
                        checked={formSettings.customRules.lastOverExemption}
                        onCheckedChange={(checked) => 
                          setFormSettings(prev => ({ 
                            ...prev, 
                            customRules: { ...prev.customRules, lastOverExemption: checked }
                          }))
                        }
                      />
                    </div>
                    
                    <div className="bg-blue-50 p-3 rounded-lg">
                      <p className="text-sm text-blue-800">
                        <strong>Example:</strong> With max {formSettings.customRules.maxBallsPerOver} balls per over, 
                        the over ends after {formSettings.customRules.maxBallsPerOver} deliveries regardless of extras.
                        {formSettings.customRules.lastOverExemption ? 
                          " The last over is exempt and follows normal cricket rules." : ""
                        }
                      </p>
                    </div>
                  </>
                )}
              </div>

              {/* Last Man Stands Rule */}
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <div>
                    <Label>Last Man Stands</Label>
                    <p className="text-sm text-gray-600">Allow last batter to bat alone when all others are out</p>
                  </div>
                  <Switch 
                    checked={formSettings.customRules.enableLastManStands}
                    onCheckedChange={(checked) => 
                      setFormSettings(prev => ({ 
                        ...prev, 
                        customRules: { ...prev.customRules, enableLastManStands: checked }
                      }))
                    }
                  />
                </div>
                
                {formSettings.customRules.enableLastManStands && (
                  <div className="bg-purple-50 p-3 rounded-lg">
                    <p className="text-sm text-purple-800">
                      <strong>Tonk Rule:</strong> When all batters are out or retired, the last batter can continue batting alone until they are out, retire, or the innings ends.
                    </p>
                  </div>
                )}
              </div>
            </div>

            {/* 3. Team Management Section */}
            <div className="space-y-3 border-t pt-6">
              <h3 className="font-medium text-lg">Team Management</h3>
              <Button
                variant="outline"
                onClick={() => setShowTeamManagement(true)}
                className="w-full"
              >
                <Users className="w-4 h-4 mr-2" />
                Manage Team Players
              </Button>
              <div className="text-sm text-gray-600 space-y-1">
                <p>• Team A: {Math.min(localTeamAPlayers.length, maxPlayersForTeam)}/{maxPlayersForTeam} players</p>
                <p>• Team B: {Math.min(localTeamBPlayers.length, maxPlayersForTeam)}/{maxPlayersForTeam} players</p>
              </div>
            </div>

            {/* Game Actions Section */}
            <div className="space-y-3 border-t pt-6">
              <h3 className="font-medium text-lg">Game Actions</h3>
              <div className="space-y-2">
                <Button
                  variant="outline"
                  onClick={() => setShowEndInningsDialog(true)}
                  className="w-full border-orange-200 text-orange-600 hover:bg-orange-50"
                >
                  <Power className="w-4 h-4 mr-2" />
                  End Current Innings
                </Button>
                <Button
                  variant="outline"
                  onClick={() => setShowEndGameDialog(true)}
                  className="w-full border-red-200 text-red-600 hover:bg-red-50"
                >
                  <Power className="w-4 h-4 mr-2" />
                  End Game
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Save Template Dialog */}
      <AlertDialog open={showSaveTemplate} onOpenChange={setShowSaveTemplate}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Save Game Template</AlertDialogTitle>
            <AlertDialogDescription>
              Save these settings as a template for future games.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="py-4">
            <Label htmlFor="templateName">Template Name</Label>
            <Input
              id="templateName"
              value={templateName}
              onChange={(e) => setTemplateName(e.target.value)}
              placeholder="Enter template name"
              className="mt-2"
            />
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleSaveTemplate} disabled={!templateName.trim()}>
              Save Template
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Save Team Dialog */}
      <AlertDialog open={showSaveTeamDialog} onOpenChange={setShowSaveTeamDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Save Team</AlertDialogTitle>
            <AlertDialogDescription>
              Save Team {activeTeam} players for future games.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="py-4">
            <Label htmlFor="teamName">Team Name</Label>
            <Input
              id="teamName"
              value={teamName}
              onChange={(e) => setTeamName(e.target.value)}
              placeholder="Enter team name"
              className="mt-2"
            />
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleSaveTeam} disabled={!teamName.trim()}>
              Save Team
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* End Innings Dialog */}
      <AlertDialog open={showEndInningsDialog} onOpenChange={setShowEndInningsDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>End Current Innings</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to end the current innings? This will switch to the next innings or end the game if it's the final innings.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleEndInnings} className="bg-orange-600 hover:bg-orange-700">
              End Innings
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* End Game Dialog */}
      <AlertDialog open={showEndGameDialog} onOpenChange={setShowEndGameDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>End Game</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to end the current game? This will save the game to history and start a new one.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleEndGame} className="bg-red-600 hover:bg-red-700">
              End Game
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}